'use client';

import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check } from 'lucide-react';
import * as React from 'react';

import { HIT_AREA_44_FROM_16 } from '@/lib/styles/touch-targets';
import { cn } from '@/lib/utils';

/**
 * Checkbox — accessible checkbox built on @radix-ui/react-checkbox.
 *
 * Touch target (WCAG 2.5.8 / #9865): the visual box stays 16×16 px
 * (`h-4 w-4`) — this primitive has ~25 consumers, several of them dense
 * lists/rows, so bumping the visual box would shift layout everywhere it's
 * used. Instead the *interactive* hit area is extended to 44×44 px via a
 * transparent `::before` pseudo-element (`HIT_AREA_44_FROM_16` —
 * `lib/styles/touch-targets.ts`), the same technique already used by
 * `Switch` (#5301). Math: 16 + 2×14 = 44 (`before:inset-[-14px]`). The
 * pseudo-element is absolutely positioned and out of flow, so it never
 * affects the parent's flex/grid layout.
 */
const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      'peer h-4 w-4 shrink-0 rounded-sm border border-brand-secondary-color ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary-color focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-brand-secondary-color data-[state=checked]:text-content-inverse',
      HIT_AREA_44_FROM_16,
      className
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn('flex items-center justify-center text-current')}
    >
      <Check className='h-4 w-4' />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
